import {getPreference} from '@netx/core-user-next/utils/getPreference';
import equal from 'fast-deep-equal';

import {GridEditor, GridEditorColumn, HypergridCellEvent, Point} from './types';

export const appendValue = (value1: any, value2: any, appendChar?: string) => {
	if (value1 == null) return value2;
	if (value2 == null) return value1;
	if (Array.isArray(value1) && Array.isArray(value2))
		return [...new Set([...value1, ...value2])];
	return `${value1}${appendChar || ''}${value2}`;
};

type BreakItUpOptions = {
	amount?: number;
	delay?: number;
	increment?: number;
	size: number;
	start?: number;
};

const defaultOptions: Required<
	Pick<BreakItUpOptions, 'start' | 'amount' | 'increment' | 'delay'>
> = {
	start: 0,
	amount: 10,
	increment: 1,
	delay: 2,
};

export async function breakItUp<T = any>(
	options: BreakItUpOptions,
	processCallback: (index: number) => false | void,
): Promise<null>;

export async function breakItUp<T = any>(
	options: BreakItUpOptions,
	processCallback: (index: number) => false | void,
	completeCallback?: () => T,
): Promise<T>;

export async function breakItUp<T = any>(
	options: BreakItUpOptions,
	processCallback: (index: number) => false | void,
	completeCallback?: () => T,
): Promise<T | null> {
	options = {...defaultOptions, ...options};
	const {amount, delay, increment, size, start} =
		options as Required<BreakItUpOptions>;

	let idx = 0;

	return new Promise((resolve, reject) => {
		// So we are not creating a new anonymouse function each chunk we iterate over (delays and all)
		const process = function (start: number, amt: number) {
			// Vars
			let processed: void | false = undefined;

			// Keep in bounds
			if (start < 0) start = 0;
			let end = start + amount;
			if (end > size) end = size;

			let i = start;
			// Process
			for (; i < end; i += increment) {
				// Return false in your process callback to end the loop and just fire the complete callback
				processed = processCallback.call(self, i);
				idx++;
				if (processed === false) break;
			}

			// Next
			if (processed !== false && i <= size - 1) {
				brokenUp(i, amount);
			}
			// Finished
			else {
				resolve(
					typeof completeCallback === 'function' ? completeCallback() : null,
				);
			}
		};
		// I am trying this out so that we might be able to give some progress indication in the event that
		// we are trying to edit a whole lot of cells (so the screen isn't just locked up)
		const brokenUp = function (start: number, amt: number) {
			// No delay on first call
			idx === 0 ? process(start, amt) : setTimeout(process, delay, start, amt);
		};
		// Start
		brokenUp(start, amount);
	});
}

export const checkEquality = (newValue: any, originalValue: any) => {
	return (
		((originalValue == null || originalValue === '') &&
			(newValue == null || newValue === '')) ||
		equal(originalValue, newValue)
	);
};

export const getAppendChar = () => {
	let appendChar = getPreference('metadata.appendChars') as string;
	if (appendChar == null) {
		appendChar = ' ';
	}
	return appendChar;
};

export const getBehaviorFromGrid = (plugin: GridEditor) => {
	return plugin.behavior;
};

export const getDataModelFromGrid = (plugin: GridEditor) => {
	return getBehaviorFromGrid(plugin).dataModel;
};

export const getDataSourceFromGrid = (plugin: GridEditor) => {
	return getDataModelFromGrid(plugin).dataSource;
};

export const getColumnFromGrid = (plugin: GridEditor, x: number) => {
	return getBehaviorFromGrid(plugin).columns[x];
};

export const getColumnsFromGrid = (plugin: GridEditor) => {
	return getBehaviorFromGrid(plugin).allColumns;
};

export const getSortIndexesFromGrid = (plugin: GridEditor) => {
	return getDataModelFromGrid(plugin).sortIndex;
};

export const getDataFromGrid = (plugin: GridEditor) => {
	return getDataSourceFromGrid(plugin).data;
};

export const getDataFromPoint = (plugin: GridEditor, point: Point) => {
	return getDataFromGrid(plugin)[point.y];
};

export const getDataPointFromCellEvent = (event: HypergridCellEvent) => {
	if (event.dataCell) {
		return event.dataCell;
	}

	const detail = event.detail;
	return detail.input !== void 0 && detail.input.event !== void 0
		? detail.input.event.dataCell
		: detail.dataCell;
};

import {FormActions, FormValues} from '@netx/core-components/Form';

export const getUpdatedValueData = (
	assetId: number,
	newValues: FormValues,
	rowData: FormValues,
	initialiValues: FormValues[],
	column: any,
	actions?: FormActions,
	appendChar?: string,
) => {
	const {name} = column;
	const action = actions?.[name] || 'overwrite';

	let newValue = newValues[name];
	if (action === 'clear') newValue = null;
	if (newValue === void 0) return;

	newValue =
		action === 'append'
			? appendValue(rowData[name], newValue, appendChar)
			: newValue;

	newValue = column.properties.parseInValue
		? column.properties.parseInValue(newValue)
		: newValue;

	const originalValue = initialiValues.find(
		(data) => data.assetId === assetId,
	)?.[name];

	return {
		value: newValue,
		changed: !checkEquality(newValue, originalValue),
	};
};

export const getColumnsByName = (columns: GridEditorColumn[]) => {
	const columnsByName: {
		[column_name: string]: any;
	} = {};
	columns.forEach((column) => {
		columnsByName[column.name] = column;
	});
	return columnsByName;
};
